// Single source of truth for the site's canonical identity.
// Production domain is iraqcert.com (the Lovable preview URL must never appear
// in canonicals, schema, sitemap, or llms.txt — mixed domains fragment the
// entity and split ranking signals).

export const SITE_URL = "https://iraqcert.com";

export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/** Stable @id anchors for the JSON-LD entity graph. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const LOCALBUSINESS_ID = `${SITE_URL}/#localbusiness`;

/**
 * Real public profiles. Feeds both the footer social icons and Organization
 * `sameAs` in the JSON-LD graph, so one edit updates both. Keep empty until a
 * profile actually exists — a link to linkedin.com's homepage is worse than none.
 */
export const SOCIAL_PROFILES: {
  label: "LinkedIn" | "X" | "Instagram" | "Facebook" | "Google Business Profile";
  href: string;
}[] = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/iraqcertification",
  },
  { label: "Instagram", href: "https://www.instagram.com/iraqcert/" },
];

/** Absolute URL for a route path. Pass "/" for the home page. */
export function absolute(path = "/"): string {
  if (!path || path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

// --- Language / URL helpers (English at root, Arabic under /ar) ---

/** Derive the active language from a route pathname. */
export function langFromPath(pathname: string): "en" | "ar" {
  return pathname === "/ar" || pathname.startsWith("/ar/") ? "ar" : "en";
}

/** The Arabic URL for an English path. "/" -> "/ar", "/about" -> "/ar/about". */
export function toArabicPath(enPath: string): string {
  if (enPath === "/" || enPath === "") return "/ar";
  return `/ar${enPath.startsWith("/") ? enPath : `/${enPath}`}`;
}

/** The English URL for any path (strips a leading /ar). */
export function toEnglishPath(pathname: string): string {
  if (pathname === "/ar") return "/";
  if (pathname.startsWith("/ar/")) return pathname.slice(3) || "/";
  return pathname || "/";
}

/** The counterpart URL in the other language, for the language switcher. */
export function switchLangPath(pathname: string): string {
  return langFromPath(pathname) === "ar"
    ? toEnglishPath(pathname)
    : toArabicPath(pathname);
}

/**
 * Bidirectional, self-referencing hreflang alternates for a page.
 * Pass the ENGLISH path; both variants + x-default are emitted.
 */
export function hreflangLinks(enPath: string) {
  return [
    { rel: "alternate", hrefLang: "en", href: absolute(enPath) },
    { rel: "alternate", hrefLang: "ar", href: absolute(toArabicPath(enPath)) },
    { rel: "alternate", hrefLang: "x-default", href: absolute(enPath) },
  ];
}
